import { Wallet, BanknoteArrowUp, Users, Package, ArrowUp, ArrowDown } from 'lucide-react';
import { StatCardData } from '@/data/mockDashboardData';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
// import { DashboardGeneralAPI } from '@/api/type';

interface StatsOverviewProps {
  stats: StatCardData[];
}

const iconConfig = [
  { Icon: Wallet, bgColor: 'bg-success/10', iconColor: 'text-success' },
  { Icon: BanknoteArrowUp, bgColor: 'bg-primary/10', iconColor: 'text-primary' },
  { Icon: Users, bgColor: 'bg-blue-500/10', iconColor: 'text-blue-500' },
  { Icon: Package, bgColor: 'bg-warning/10', iconColor: 'text-warning' },
];

const chartColors = ['#10b981', '#BF9B30', '#3b82f6', '#f59e0b'];
const gradientColors = ['#10b981', '#BF9B30', '#3b82f6', '#f59e0b'];

export const StatsOverview = ({ stats }: StatsOverviewProps) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => {
        const { Icon, bgColor, iconColor } = iconConfig[index];
        const chartData = stat.chartData?.map((value) => ({ value })) || [];

        return (
          <div
            key={index}
            className="!bg-white dark:!bg-dark-card rounded-md shadow-sm border border-gray-200 dark:border-dark-border hover:shadow-md transition-shadow overflow-hidden"
          >
            <div className="p-6 pb-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h5 className="text-sm !text-gray-600 dark:!text-dark-text-secondary font-bold uppercase mb-3">
                    {stat.label}
                  </h5>
                  <h3 className="text-2xl font-bold !text-gray-900 dark:!text-white mb-2">
                    {stat.value}
                    {index < 2 && <span className="text-sm ml-1">đ</span>}
                    {index === 2 && <span className="text-sm ml-1">{t('home.unit_people')}</span>}
                    {index === 3 && <span className="text-sm ml-1">{t('home.unit_packages')}</span>}
                  </h3>
                  {stat.trend && (
                    <p className="mb-0 text-sm">
                      <span className={`${stat.trend.isPositive ? 'text-success' : 'text-danger'} inline-flex items-center gap-1 mr-1`}>
                        {stat.trend.isPositive ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )}
                        {stat.trend.percentage}%
                      </span>
                      <span className="!text-gray-600 dark:!text-dark-text-secondary text-xs whitespace-nowrap">
                        {stat.trend.text}
                      </span>
                    </p>
                  )}
                </div>
                <div className={`${bgColor} dark:${bgColor} p-3 rounded-md flex-shrink-0`}>
                  <Icon className={`w-7 h-7 ${iconColor}`} />
                </div>
              </div>
            </div>

            {chartData.length > 0 && (
              <div className="h-18 -mb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <defs>
                      <linearGradient id={`gradientColor-${index}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={gradientColors[index]} stopOpacity={0.5} />
                        <stop offset="100%" stopColor={gradientColors[index]} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={chartColors[index]}
                      strokeWidth={2}
                      fill={`url(#gradientColor-${index})`}
                      animationDuration={500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
